import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { TrendingUp, TrendingDown, DollarSign, Users, HardDrive, CreditCard, Loader2 } from 'lucide-react';
import CohortRetentionTable from './CohortRetentionTable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RANGE_OPTIONS = [
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' },
];

const Sparkline = ({ data, color = '#8B5CF6' }) => (
  <ResponsiveContainer width="100%" height={40}>
    <LineChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 2 }}>
      <Line
        type="monotone"
        dataKey="value"
        stroke={color}
        strokeWidth={1.5}
        dot={false}
        isAnimationActive={false}
      />
    </LineChart>
  </ResponsiveContainer>
);

const KpiCard = ({ title, current, allTime, changePct, sparkData, sparkColor, icon: Icon, unit }) => {
  const changeColor = (() => {
    if (changePct === null || changePct === undefined) return 'text-[var(--isp-muted)]';
    if (changePct > 0) return 'text-emerald-400';
    if (changePct < 0) return 'text-red-400';
    return 'text-[var(--isp-muted)]';
  })();
  const ChangeIcon = changePct > 0 ? TrendingUp : TrendingDown;

  return (
    <div
      className=" border border-[var(--isp-glass-border)] bg-slate-900/60 backdrop-blur-xl p-5 flex flex-col gap-3"
      data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
            <Icon className="w-4 h-4 text-purple-400" />
          </div>
          <span className="text-xs font-medium text-[var(--isp-body)]">{title}</span>
        </div>
        {changePct !== null && changePct !== undefined && (
          <span className={`flex items-center gap-0.5 text-[11px] font-semibold ${changeColor}`}>
            <ChangeIcon className="w-3 h-3" />
            {Math.abs(changePct)}%
          </span>
        )}
      </div>

      <div>
        <div className="text-2xl font-bold text-white leading-none">
          {unit === '$' && '$'}{Number(current).toLocaleString(undefined, { maximumFractionDigits: 2 })}
          {unit === 'MB' && <span className="text-sm text-[var(--isp-muted)] ml-1">MB</span>}
        </div>
        <div className="text-[11px] text-[var(--isp-muted)] mt-1">
          All-time: {unit === '$' && '$'}{Number(allTime).toLocaleString(undefined, { maximumFractionDigits: 2 })}
          {unit === 'MB' && ' MB'}
        </div>
      </div>

      {sparkData && sparkData.length > 0 && (
        <div className="-mx-2 -mb-2">
          <Sparkline data={sparkData} color={sparkColor} />
        </div>
      )}
    </div>
  );
};

const ChartCard = ({ title, subtitle, children, testId }) => (
  <div
    className=" border border-[var(--isp-glass-border)] bg-slate-900/60 backdrop-blur-xl p-5"
    data-testid={testId}
  >
    <div className="mb-4">
      <h3 className="text-sm font-semibold text-white">{title}</h3>
      {subtitle && <p className="text-[11px] text-[var(--isp-muted)] mt-0.5">{subtitle}</p>}
    </div>
    <div className="h-64">{children}</div>
  </div>
);

const axisProps = {
  stroke: 'rgba(255,255,255,0.25)',
  tick: { fill: 'rgba(255,255,255,0.5)', fontSize: 10 },
  tickLine: false,
  axisLine: false,
};

const tooltipContentStyle = {
  background: 'rgba(15,23,42,0.95)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '8px',
  color: '#fff',
  fontSize: '12px',
};

const formatDate = (d) => {
  if (!d) return '';
  const parts = d.split('-');
  return `${parts[1]}/${parts[2]}`;
};

export const HealthTab = () => {
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/admin/health-metrics`, { params: { days } });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) {
          toast.error(e.response?.data?.detail || 'Failed to load health metrics');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [days]);

  const revenueData = useMemo(
    () => (data?.series?.revenue_usd || []).map(d => ({ ...d, date: formatDate(d.date) })),
    [data],
  );
  const dauData = useMemo(
    () => (data?.series?.daily_active_users || []).map(d => ({ ...d, date: formatDate(d.date) })),
    [data],
  );
  const storageData = useMemo(
    () => (data?.series?.storage_mb || []).map(d => ({ ...d, date: formatDate(d.date) })),
    [data],
  );

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-20 text-[var(--isp-body)]" data-testid="health-loading">
        <Loader2 className="w-6 h-6 animate-spin" />
        <span className="ml-2">Loading health metrics…</span>
      </div>
    );
  }

  if (!data) return null;

  const { kpis } = data;

  return (
    <div className="space-y-6" data-testid="health-tab">
      {/* Range selector */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-white">Platform Health</h2>
          <p className="text-xs text-[var(--isp-muted)] mt-0.5">Birds-eye view of revenue, engagement, and storage.</p>
        </div>
        <div className="flex gap-1 p-1 rounded-xl border border-[var(--isp-glass-border)] bg-slate-900/60">
          {RANGE_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setDays(opt.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                days === opt.value
                  ? 'bg-white/10 text-white'
                  : 'text-[var(--isp-body)] hover:bg-[var(--isp-glass-inner)]'
              }`}
              data-testid={`health-range-${opt.value}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          title="Revenue"
          current={kpis.mrr.current}
          allTime={kpis.mrr.all_time}
          changePct={kpis.mrr.change_pct}
          sparkData={revenueData}
          sparkColor="#10B981"
          icon={DollarSign}
          unit="$"
        />
        <KpiCard
          title="Active Users"
          current={kpis.active_users.current}
          allTime={kpis.active_users.all_time}
          changePct={kpis.active_users.change_pct}
          sparkData={dauData}
          sparkColor="#3B82F6"
          icon={Users}
        />
        <KpiCard
          title="Storage"
          current={kpis.storage.current}
          allTime={kpis.storage.all_time}
          changePct={kpis.storage.change_pct}
          sparkData={storageData}
          sparkColor="#F59E0B"
          icon={HardDrive}
          unit="MB"
        />
        <KpiCard
          title="Subscriptions"
          current={kpis.subscriptions.current}
          allTime={kpis.subscriptions.all_time}
          changePct={kpis.subscriptions.change_pct}
          icon={CreditCard}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Revenue Trend (USD)" subtitle={`Completed payments, last ${days} days`} testId="chart-revenue">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={revenueData}>
              <defs>
                <linearGradient id="revGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="date" {...axisProps} />
              <YAxis {...axisProps} />
              <Tooltip contentStyle={tooltipContentStyle} formatter={(v) => [`$${Number(v).toFixed(2)}`, 'Revenue']} />
              <Area type="monotone" dataKey="value" stroke="#10B981" strokeWidth={2} fill="url(#revGradient)" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Daily Active Users" subtitle={`Unique logins per day, last ${days} days`} testId="chart-dau">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={dauData}>
              <defs>
                <linearGradient id="dauGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="date" {...axisProps} />
              <YAxis {...axisProps} />
              <Tooltip contentStyle={tooltipContentStyle} formatter={(v) => [v, 'Users']} />
              <Area type="monotone" dataKey="value" stroke="#3B82F6" strokeWidth={2} fill="url(#dauGradient)" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Storage Growth"
          subtitle={`MB added per day (docs + audio), last ${days} days`}
          testId="chart-storage"
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={storageData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="date" {...axisProps} />
              <YAxis {...axisProps} />
              <Tooltip contentStyle={tooltipContentStyle} formatter={(v) => [`${Number(v).toFixed(2)} MB`, 'Added']} />
              <Bar dataKey="value" fill="#F59E0B" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Cumulative Revenue" subtitle="Running total of revenue over window" testId="chart-cumulative">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={revenueData.reduce((acc, d, i) => {
                const prev = i > 0 ? acc[i - 1].value : 0;
                acc.push({ date: d.date, value: Number((prev + d.value).toFixed(2)) });
                return acc;
              }, [])}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="date" {...axisProps} />
              <YAxis {...axisProps} />
              <Tooltip contentStyle={tooltipContentStyle} formatter={(v) => [`$${v}`, 'Cumulative']} />
              <Line type="monotone" dataKey="value" stroke="#8B5CF6" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* iter197 — Day-N retention table. Cohort = ISO week of signup;
          rates = % active by day-offset. Sits at the bottom of the
          Health tab so the existing KPI grid + revenue charts stay
          above the fold; analytics teams scroll here intentionally. */}
      <CohortRetentionTable />
    </div>
  );
};
